import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  Gavel as LegalIcon,
  Security as SecurityIcon,
  AccountBalance as FinanceIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import SEOHead from './common/SEOHead';

const TermsOfService: React.FC = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const termsStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms of Service - altifi",
    "description": "Terms of Service for altifi personal finance management application. Review our terms and conditions for using our budgeting and financial tracking platform.",
    "url": "https://altifi.com/terms-of-service",
    "isPartOf": {
      "@type": "WebSite",
      "name": "altifi",
      "url": "https://altifi.com"
    },
    "provider": {
      "@type": "Organization",
      "name": "altifi"
    }
  };

  return (
    <>
      <SEOHead 
        title="Terms of Service - altifi | Legal Terms and Conditions"
        description="Terms of Service for altifi personal finance management application. Review our terms and conditions for using our budgeting and financial tracking platform."
        keywords="terms of service, legal, conditions, altifi, personal finance, user agreement, privacy, financial data"
        canonicalUrl="https://altifi.com/terms-of-service"
        structuredData={termsStructuredData}
      />
      <Box
        sx={{
          minHeight: '100vh',
          background: (theme) => theme.palette.mode === 'light'
            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[900]} 100%)`,
          py: 4
        }}
      >
        <Container maxWidth="md">
          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              background: (theme) => theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(30, 30, 30, 0.9)',
              backdropFilter: 'blur(20px)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 3
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <LegalIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h3" component="h1" fontWeight={700}>
                  Terms of Service
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Last updated: {currentDate}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" paragraph>
              Welcome to <strong>altifi</strong>, a personal finance management platform designed to help you take control of your financial future. 
              These Terms of Service ("Terms") govern your use of our application and services.
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip icon={<SecurityIcon />} label="Data Protection" size="small" />
              <Chip icon={<FinanceIcon />} label="Financial Privacy" size="small" />
              <Chip icon={<UpdateIcon />} label="Regular Updates" size="small" />
            </Box>
          </Paper>

          {/* Terms Content */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: (theme) => theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(30, 30, 30, 0.9)',
              backdropFilter: 'blur(20px)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 3
            }}
          >
            {/* 1. Acceptance of Terms */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing or using altifi ("the Service"), you agree to be bound by these Terms of Service and our Privacy Policy. 
              If you do not agree to these terms, please do not use our service.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 2. Description of Service */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              2. Description of Service
            </Typography>
            <Typography variant="body1" paragraph>
              altifi is a personal finance management application that provides tools for:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Budget creation and management" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Income and expense tracking" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Financial goal setting and monitoring" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Financial insights and reporting" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Data visualization and analytics" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 3. User Accounts */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              3. User Accounts
            </Typography>
            <Typography variant="body1" paragraph>
              To use our Service, you must create an account. You agree to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Provide accurate and complete registration information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Maintain the security of your password and account" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Promptly notify us of any unauthorized use of your account" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Be responsible for all activities that occur under your account" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 4. Financial Data and Privacy */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              4. Financial Data and Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Data Ownership:</strong> You retain ownership of all financial data you input into altifi. 
              We do not claim ownership of your personal financial information.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Data Security:</strong> We implement industry-standard security measures to protect your data, including:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Encrypted data transmission (HTTPS/TLS)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Secure password hashing and storage" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Regular security audits and updates" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Limited access to personal data by authorized personnel only" />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              <strong>No Bank Access:</strong> altifi does not connect to your bank accounts or financial institutions. 
              All financial data is manually entered by you, ensuring complete control over what information is shared.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 5. Acceptable Use */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              5. Acceptable Use
            </Typography>
            <Typography variant="body1" paragraph>
              You agree not to use the Service to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Violate any applicable laws or regulations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Transmit malicious code or attempt to compromise system security" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Reverse engineer or attempt to extract source code" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Use the service for illegal financial activities" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Share your account credentials with others" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Interfere with other users' use of the service" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 6. Service Availability */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              6. Service Availability
            </Typography>
            <Typography variant="body1" paragraph>
              We strive to maintain high service availability, but we do not guarantee uninterrupted access. 
              The Service may be temporarily unavailable due to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Scheduled maintenance and updates" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Technical difficulties or server issues" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Third-party service dependencies" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Extraordinary circumstances beyond our control" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 7. Limitation of Liability */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              7. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Financial Decisions:</strong> altifi is a tool to help you track and manage your finances. 
              We do not provide financial advice, and you are solely responsible for your financial decisions.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Data Accuracy:</strong> While we strive to ensure our service works correctly, 
              you are responsible for verifying the accuracy of your financial data and calculations.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Limitation:</strong> To the maximum extent permitted by law, altifi shall not be liable for any 
              indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, 
              data, or financial losses resulting from your use of the service.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 8. Data Export and Account Termination */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              8. Data Export and Account Termination
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Your Rights:</strong> You may terminate your account at any time through your account settings. 
              Upon termination, you may export your data for your personal records.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Data Retention:</strong> After account termination, we will retain your data for a reasonable period 
              to allow for account recovery, then securely delete all personal information in accordance with our Privacy Policy.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Service Termination:</strong> We reserve the right to terminate accounts that violate these Terms 
              or engage in prohibited activities.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 9. Updates to Terms */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              9. Updates to Terms
            </Typography>
            <Typography variant="body1" paragraph>
              We may update these Terms periodically to reflect changes in our service or legal requirements. 
              When we make significant changes, we will:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Notify users via email or in-app notification" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Update the 'Last updated' date at the top of this page" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide a reasonable notice period before changes take effect" />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              Continued use of the service after changes constitute acceptance of the updated Terms.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 10. Intellectual Property */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              10. Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph>
              The altifi service, including its design, functionality, and content, is protected by copyright, 
              trademark, and other intellectual property laws. You may not reproduce, distribute, or create 
              derivative works without our express written permission.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 11. Governing Law */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              11. Governing Law
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms are governed by and construed in accordance with the laws of the jurisdiction where 
              altifi operates, without regard to conflict of law principles.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 12. Contact Information */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              12. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about these Terms of Service, please contact us:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Email: support@altifi.com" 
                  secondary="For general questions and support"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Legal: legal@altifi.com" 
                  secondary="For legal matters and privacy concerns"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Support Center" 
                  secondary="Visit our Support page for additional help resources"
                />
              </ListItem>
            </List>

            <Box 
              sx={{ 
                mt: 4, 
                p: 3, 
                background: (theme) => theme.palette.mode === 'light' 
                  ? theme.palette.grey[50] 
                  : theme.palette.grey[900],
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`
              }}
            >
              <Typography variant="body2" color="text.secondary" align="center">
                By using altifi, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service 
                and our Privacy Policy. Thank you for trusting altifi with your personal finance management.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default TermsOfService; 